import clsx from 'clsx';

import styled from '@emotion/styled';

import { capitalize, formatForStatus } from '../../utils/utils';
import DotIcon from '../Icons/DotIcon';

// import { capitalize, formatForStatus } from '../../../utils/utils';

interface StatusCellProps {
  data?: Record<string, any>;
  statusKey?: string;
  plain?: boolean;
  customClasses?: string;
  useBoolean?: boolean;
  hasDot?: boolean;
  positiveType?: boolean;
}

const StatusCell: React.FC<StatusCellProps> = ({
  data,
  statusKey,
  plain = false,
  customClasses,
  useBoolean,
  hasDot,
  positiveType,
}) => {
  let label = data && data[statusKey ?? ''];
  const trueCase = positiveType ? 'positive' : 'active';
  const falseCase = positiveType ? 'negative' : 'inactive';

  if ((label === undefined || label === null || label === '') && !useBoolean) {
    label = statusKey;
  }

  if (data && typeof data['status'] === 'boolean') {
    label = Boolean(data['status']) ? trueCase : falseCase;
  }

  let formatStatus = formatForStatus(String(label) ?? '');

  if (plain) {
    formatStatus = label;
  }

  return (
    <StatusCellWrapper
      className={clsx(
        `${formatForStatus(String(label) ?? '')} status`,
        customClasses,
      )}
    >
      {hasDot && <DotIcon />}
      {capitalize(formatStatus)}
    </StatusCellWrapper>
  );
};

export default StatusCell;

const StatusCellWrapper = styled.div`
  display: flex;
  background-color: var(--sunk);
  width: fit-content;
  align-items: center;
  gap: 6px;
  line-height: 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
  padding: 5px 11px !important;
  user-select: none;
  height: fit-content;

  &.small-border-radius {
    border-radius: 8px;
  }

  &.declined {
    color: ${({ theme }) => theme.palette.errorRed};
    background-color: rgba(217, 45, 32, 0.1);
  }

  &.positive,
  &.confirmed,
  &.successful,
  &.delivered,
  &.succeeded,
  &.completed {
    color: ${({ theme }) => theme.palette.greenColor};
    background-color: rgba(31, 122, 69, 0.05);
    border: 1px solid rgba(31, 122, 69, 0.1);
  }

  &.open {
    color: ${({ theme }) => theme.palette.errorRed};
    background-color: rgba(217, 45, 32, 0.06);
    border: 1px solid rgba(217, 45, 32, 0.15);
  }

  &.acknowledged,
  &.scheduled {
    color: #0B7A5A;
    background-color: #E8F4EE;
    border: 1px solid ${({ theme }) => theme.palette.mediumBorder};
  }

  &.cleared {
    background-color: rgba(31, 122, 69, 0.1);
    color: #1F7A45;
  }

  &.dismissed,
  &.draft,
  &.cancelled,
  &.unclaimed,
  &.skipped,
  &.none {
    color: ${({ theme }) => theme.palette.grayTextColor};
    background-color: ${({ theme }) => theme.palette.veryLightGrayBg};
  }

  &.critical,
  &.down {
    color: white;
    background-color: ${({ theme }) => theme.palette.errorRed};
  }

  &.high {
    color: ${({ theme }) => theme.palette.errorRed};
    background-color: rgba(217, 45, 32, 0.1);
    border: 1px solid rgba(217, 45, 32, 0.2);
  }

  &.medium,
  &.degraded,
  &.frozen,
  &.paused {
    color: #C2410C;
    background-color: rgba(194, 65, 12, 0.1);
  }

  &.low {
    color: #0B7A5A;
    background-color: #E8F4EE;
    border: 1px solid ${({ theme }) => theme.palette.mediumBorder};
  }

  &.healthy {
    color: ${({ theme }) => theme.palette.greenColor};
    background-color: rgba(31, 122, 69, 0.05);
    border: 1px solid rgba(31, 122, 69, 0.1);
  }

  &.failed,
  &.reversed,
  &.payment-failed,
  &.payment-reversed,
  &.delivery-failed,
  &.undeliverable,
  &.revoked {
    color: ${({ theme }) => theme.palette.errorRed};
    background-color: rgba(217, 45, 32, 0.1);
  }

  &.processing,
  &.initiated,
  &.reversal-pending,
  &.paying,
  &.delivering,
  &.charging,
  &.ready {
    background-color: rgba(202, 138, 4, 0.1);
    color: #A16207;
  }

  &.exclusive,
  &.processed,
  &.required,
  &.unknown {
    color: #0B7A5A;
    background-color: #E8F4EE;
    border: 1px solid ${({ theme }) => theme.palette.mediumBorder};
  }

  &.gig,
  &.hiring-conflict,
  &.notinitialized,
  &.not-initialized {
    color: #C2410C;
    background-color: rgba(194, 65, 12, 0.1);
    border: 1px solid ${({ theme }) => theme.palette.mediumBorder};
  }

  &.pending,
  &.theft {
    background-color: rgba(202, 138, 4, 0.1);
    color: #A16207;
  }

  &.accepted,
  &.active,
  &.resolved,
  &.percentage,
  &.true {
    background-color: rgba(31, 122, 69, 0.1);
    color: #1F7A45;
  }

  &.rejected,
  &.accident,
  &.negative,
  &.false {
    background-color: rgba(217, 45, 32, 0.1);
    color: #D92D20;
  }

  &.exited,
  &.unemployed,
  &.delisted {
    background-color: rgba(79, 90, 82, 0.05);
    border: 1px solid rgba(79, 90, 82, 0.1);
    color: ${({ theme }) => theme.palette.grayTextColor};
  }
`;
